import { useCallback, useEffect, useRef, useState } from "react";
import { autoCrossCells } from "../../shared/nonogram/autoCross";
import type { CellMark, Puzzle } from "../../shared/nonogram/types";
import { track } from "../lib/analytics";
import {
  clearGame,
  isReturningVisit,
  loadAssistSettings,
  loadGame,
  loadPlayMode,
  loadStats,
  recordCompletion,
  saveAssistSettings,
  saveGame,
  savePlayMode,
  type AssistSettings,
  type PlayMode,
  type PlayerStats,
  type Snapshot,
} from "../lib/storage";

export type Tool = "fill" | "cross" | "erase";

/** What a touch on the board does. Picking a marking tool always implies "draw",
    so the two live together and only one of them can be true at a time. */
export type BoardMode = "draw" | "move";

type GameOptions = {
  puzzle: Puzzle;
  sessionId: string;
  mode: "daily" | "standard";
  dailyDate?: string;
  /**
   * A game that leaves no trace: nothing saved, nothing counted, nothing reported.
   *
   * The art studio plays an unfinished drawing through the real board, which is the
   * point — a playtest should feel like the thing a player gets. What it must not do
   * is write to the player's own record. It used to file a best time against a
   * `playtest-…` id, add to the totals, and send `puzzle_start` and `puzzle_complete`
   * like any other session; and because the saved game was keyed by the piece's size
   * and slug, editing the drawing and playing it again restored the *old* progress
   * against the new solution — `loadGame` only checks that the cell count matches.
   */
  sandbox?: boolean;
};

const MISTAKE_FLASH_MS = 900;

function toolMark(tool: Tool): CellMark {
  if (tool === "fill") return 1;
  if (tool === "cross") return -1;
  return 0;
}

export type PaintMode = "toggle" | "absolute";

/** Toggle is for single taps; absolute is for a locked drag stroke that never flips mid-stroke. */
export function nextMarkForPaint(existing: CellMark, tool: Tool, mode: PaintMode = "toggle"): CellMark {
  const mark = toolMark(tool);
  if (mode === "absolute" || tool === "erase") return mark;
  return existing === mark ? 0 : mark;
}

function solved(cells: readonly CellMark[], solution: readonly number[]): boolean {
  return solution.every((expected, index) => (cells[index] === 1 ? 1 : 0) === expected);
}

function restorePlayableGame(sessionId: string, expectedCells: number, mode: GameOptions["mode"]) {
  const saved = loadGame(sessionId, expectedCells);
  // A finished standard game belongs in the collection, not on the board. Restoring
  // it would make Play another open an already-solved grid whenever the catalogue
  // recycles. Daily still restores: today's picture should stay revealed.
  if (saved?.completed && mode !== "daily") return undefined;
  return saved;
}

export function useGame({ puzzle, sessionId, mode, dailyDate, sandbox = false }: GameOptions) {
  const restored = useRef(sandbox ? undefined : restorePlayableGame(sessionId, puzzle.solution.length, mode));
  const [cells, setCells] = useState<CellMark[]>(
    () => restored.current?.cells ?? Array<CellMark>(puzzle.solution.length).fill(0),
  );
  const [history, setHistory] = useState<Snapshot[]>(() => restored.current?.history ?? []);
  const [redoStack, setRedoStack] = useState<Snapshot[]>([]);
  const [elapsedSeconds, setElapsedSeconds] = useState(() => restored.current?.elapsedSeconds ?? 0);
  const [mistakes, setMistakes] = useState(() => restored.current?.mistakes ?? 0);
  const [started, setStarted] = useState(() => restored.current?.started ?? false);
  const [completed, setCompleted] = useState(() => restored.current?.completed ?? false);
  const [tool, setTool] = useState<Tool>("fill");
  const [boardMode, setBoardMode] = useState<BoardMode>("draw");
  const [playMode, setPlayModeState] = useState<PlayMode>(() => loadPlayMode());
  const [assists, setAssistsState] = useState<AssistSettings>(() => loadAssistSettings());
  const [stats, setStats] = useState<PlayerStats>(() => loadStats());
  const [mistakeCells, setMistakeCells] = useState<number[]>([]);
  const [resumed, setResumed] = useState(() => (restored.current?.started ?? false) && !(restored.current?.completed ?? false));
  const [checkMessage, setCheckMessage] = useState("");

  // One selection, two pieces of state: reaching for a marking tool is also how you
  // leave Move, whether the reach is a tap or the F/X/E keys.
  const selectTool = useCallback((next: Tool) => {
    setTool(next);
    setBoardMode("draw");
  }, []);

  // The board is edited many times per drag, so the latest marks live in a ref and every
  // update stays a pure function of it. Deriving mistakes inside a setState updater made
  // the count depend on how often React chose to replay that updater.
  const cellsRef = useRef(cells);
  const completedRef = useRef(completed);
  const playModeRef = useRef(playMode);
  const mistakesRef = useRef(mistakes);
  const strokeSnapshot = useRef<Snapshot | undefined>(undefined);
  const completionRecorded = useRef(restored.current?.completed ?? false);
  const mistakeTimer = useRef<number | undefined>(undefined);

  const elapsedBase = useRef(elapsedSeconds);
  const runningSince = useRef<number | undefined>(undefined);
  const assistsRef = useRef(assists);

  useEffect(() => {
    playModeRef.current = playMode;
  }, [playMode]);

  useEffect(() => {
    assistsRef.current = assists;
  }, [assists]);

  useEffect(() => {
    mistakesRef.current = mistakes;
  }, [mistakes]);

  const readClock = useCallback(
    () => elapsedBase.current + (runningSince.current ? Math.floor((Date.now() - runningSince.current) / 1000) : 0),
    [],
  );

  // Wall-clock timestamps instead of counting interval ticks: background tabs throttle
  // timers, so tick counting drifts, and a puzzle left open in a hidden tab should not
  // keep charging the player for time they were not there.
  useEffect(() => {
    if (!started || completed) {
      if (runningSince.current) {
        elapsedBase.current = readClock();
        runningSince.current = undefined;
        setElapsedSeconds(elapsedBase.current);
      }
      return undefined;
    }

    function resume(): void {
      if (runningSince.current === undefined) runningSince.current = Date.now();
    }
    function pause(): void {
      if (runningSince.current === undefined) return;
      elapsedBase.current = readClock();
      runningSince.current = undefined;
      setElapsedSeconds(elapsedBase.current);
    }
    function onVisibilityChange(): void {
      if (document.visibilityState === "hidden") pause();
      else resume();
    }

    resume();
    const timer = window.setInterval(() => setElapsedSeconds(readClock()), 500);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      pause();
    };
  }, [completed, readClock, started]);

  useEffect(() => () => window.clearTimeout(mistakeTimer.current), []);

  // The tool buttons advertise F / X / ⌫, so the keys have to select a tool from
  // anywhere on the page: they used to reach only a cell that already held focus,
  // which left the badges dead on the path everyone actually takes.
  useEffect(() => {
    if (completed) return undefined;
    function onKeyDown(event: KeyboardEvent): void {
      const target = event.target as HTMLElement | null;
      if (target?.isContentEditable) return;
      if (target && ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName)) return;

      const mod = event.metaKey || event.ctrlKey;
      if (mod && event.key.toLowerCase() === "z") {
        event.preventDefault();
        if (event.shiftKey) redoRef.current();
        else undoRef.current();
        return;
      }
      if (mod && event.key.toLowerCase() === "y") {
        event.preventDefault();
        redoRef.current();
        return;
      }
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      const key = event.key.toLowerCase();
      if (key === "f") selectTool("fill");
      else if (key === "x") selectTool("cross");
      else if (key === "e" || event.key === "Backspace" || event.key === "Delete") selectTool("erase");
      else if (key === "m") setBoardMode("move");
      else return;
      if (event.key === "Backspace") event.preventDefault();
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [completed]);

  useEffect(() => {
    if (sandbox) return;
    saveGame(sessionId, {
      version: 2,
      cells,
      history,
      elapsedSeconds,
      mistakes,
      started,
      completed,
    });
  }, [cells, completed, elapsedSeconds, history, mistakes, sandbox, sessionId, started]);

  useEffect(() => {
    if (!completed || completionRecorded.current) return;
    completionRecorded.current = true;
    if (sandbox) return;
    const nextStats = recordCompletion(puzzle.id, elapsedSeconds, dailyDate);
    setStats(nextStats);
    track("puzzle_complete", {
      puzzle_id: puzzle.id,
      size: puzzle.size,
      difficulty: puzzle.difficulty,
      duration_bucket: Math.ceil(elapsedSeconds / 60),
      mistake_bucket: Math.min(mistakes, 5),
      // Not an identifier: a flag the browser already holds, so a week's returning
      // completers can be summed without anyone being identifiable across visits.
      is_returning: isReturningVisit(),
    });
    if (mode === "daily" && dailyDate) {
      track("daily_complete", { date_utc: dailyDate, size: puzzle.size, streak_bucket: Math.min(nextStats.dailyStreak, 7) });
    }
  }, [completed, dailyDate, elapsedSeconds, mistakes, mode, puzzle, sandbox, stats.dailyStreak]);

  const ensureStarted = useCallback(() => {
    if (started) return;
    setStarted(true);
    if (sandbox) return;
    track("puzzle_start", {
      puzzle_id: puzzle.id,
      size: puzzle.size,
      difficulty: puzzle.difficulty,
      mode,
    });
  }, [mode, puzzle, sandbox, started]);

  const flashMistakes = useCallback((indices: number[]) => {
    if (indices.length === 0) {
      setMistakeCells([]);
      return;
    }
    setMistakeCells(indices);
    window.clearTimeout(mistakeTimer.current);
    mistakeTimer.current = window.setTimeout(() => setMistakeCells([]), MISTAKE_FLASH_MS);
  }, []);

  const flagMistake = useCallback((index: number) => {
    setMistakes((count) => count + 1);
    flashMistakes([index]);
  }, [flashMistakes]);

  const beginStroke = useCallback(() => {
    strokeSnapshot.current = { cells: cellsRef.current, mistakes: mistakesRef.current };
    setResumed(false);
    setCheckMessage("");
    ensureStarted();
  }, [ensureStarted]);

  const paintCell = useCallback(
    (index: number, explicitTool: Tool = tool, mode: PaintMode = "toggle", forcedMark?: CellMark) => {
      if (completedRef.current || index < 0 || index >= puzzle.solution.length) return;
      const current = cellsRef.current;
      const existing = current[index] ?? 0;
      // A forced mark is the tap-cycle assist speaking: the next state comes from
      // the cell itself (empty → fill → cross → empty) rather than the tool.
      const nextMark = forcedMark ?? nextMarkForPaint(existing, explicitTool, mode);
      if (existing === nextMark) return;

      const next = [...current];
      next[index] = nextMark;
      if (playModeRef.current === "challenge") {
        if ((nextMark === 1 && puzzle.solution[index] !== 1) || (nextMark === -1 && puzzle.solution[index] === 1)) {
          flagMistake(index);
        }
      }
      if (solved(next, puzzle.solution)) {
        for (let cellIndex = 0; cellIndex < next.length; cellIndex += 1) {
          if (next[cellIndex] === 0 && puzzle.solution[cellIndex] === 0) next[cellIndex] = -1;
        }
        completedRef.current = true;
        setCompleted(true);
      }
      cellsRef.current = next;
      setCells(next);
    },
    [flagMistake, puzzle.solution, tool],
  );

  /** Where a tap-cycle tap sends the cell: fill, then cross, then back to empty. */
  const cycleMark = useCallback((existing: CellMark): CellMark => (existing === 0 ? 1 : existing === 1 ? -1 : 0), []);

  const endStroke = useCallback(() => {
    // The auto-cross assist runs inside the stroke: its crosses belong to the marks
    // that earned them, so one Undo takes back the fill and its consequences
    // together, the way they would go back together on paper.
    if (assistsRef.current.autoCrossLines) {
      const crossed = autoCrossCells(cellsRef.current, puzzle.size, puzzle.rowClues, puzzle.columnClues);
      if (crossed) {
        cellsRef.current = crossed;
        setCells(crossed);
      }
    }
    const snapshot = strokeSnapshot.current;
    strokeSnapshot.current = undefined;
    if (snapshot && snapshot.cells !== cellsRef.current) {
      setHistory((items) => [...items.slice(-49), snapshot]);
      setRedoStack([]);
    }
  }, [puzzle.columnClues, puzzle.rowClues, puzzle.size]);

  const makeMove = useCallback((index: number, explicitTool?: Tool) => {
    beginStroke();
    paintCell(index, explicitTool);
    endStroke();
  }, [beginStroke, endStroke, paintCell]);

  const undo = useCallback(() => {
    const previous = history.at(-1);
    if (!previous) return;
    const currentSnapshot: Snapshot = { cells: cellsRef.current, mistakes: mistakesRef.current };
    cellsRef.current = previous.cells;
    completedRef.current = false;
    completionRecorded.current = false;
    setCells(previous.cells);
    setMistakes(previous.mistakes);
    setCompleted(false);
    setMistakeCells([]);
    setCheckMessage("");
    setHistory((items) => items.slice(0, -1));
    setRedoStack((items) => [...items, currentSnapshot]);
  }, [history]);

  const redo = useCallback(() => {
    const next = redoStack.at(-1);
    if (!next) return;
    const currentSnapshot: Snapshot = { cells: cellsRef.current, mistakes: mistakesRef.current };
    cellsRef.current = next.cells;
    const isSolved = solved(next.cells, puzzle.solution);
    completedRef.current = isSolved;
    if (isSolved) completionRecorded.current = false;
    setCells(next.cells);
    setMistakes(next.mistakes);
    setCompleted(isSolved);
    setMistakeCells([]);
    setCheckMessage("");
    setRedoStack((items) => items.slice(0, -1));
    setHistory((items) => [...items, currentSnapshot]);
  }, [puzzle.solution, redoStack]);

  const undoRef = useRef(undo);
  const redoRef = useRef(redo);
  useEffect(() => {
    undoRef.current = undo;
    redoRef.current = redo;
  }, [undo, redo]);

  const checkBoard = useCallback(() => {
    if (completedRef.current || playModeRef.current !== "relaxed") return;
    ensureStarted();
    const wrong: number[] = [];
    const current = cellsRef.current;
    for (let index = 0; index < puzzle.solution.length; index += 1) {
      const mark = current[index] ?? 0;
      const expected = puzzle.solution[index] ?? 0;
      if (mark === 1 && expected !== 1) wrong.push(index);
      else if (mark === -1 && expected === 1) wrong.push(index);
    }
    if (wrong.length === 0) {
      setCheckMessage("No mistakes in the marks you placed.");
      flashMistakes([]);
      return;
    }
    setMistakes((count) => count + wrong.length);
    flashMistakes(wrong);
    setCheckMessage(`${wrong.length} mistake${wrong.length === 1 ? "" : "s"} found.`);
  }, [ensureStarted, flashMistakes, puzzle.solution]);

  const setPlayMode = useCallback((next: PlayMode) => {
    setPlayModeState(next);
    savePlayMode(next);
    setCheckMessage("");
    setMistakeCells([]);
  }, []);

  const reset = useCallback(() => {
    const empty = Array<CellMark>(puzzle.solution.length).fill(0);
    cellsRef.current = empty;
    completedRef.current = false;
    completionRecorded.current = false;
    elapsedBase.current = 0;
    runningSince.current = undefined;
    setCells(empty);
    setHistory([]);
    setRedoStack([]);
    setElapsedSeconds(0);
    setMistakes(0);
    setStarted(false);
    setCompleted(false);
    setMistakeCells([]);
    setCheckMessage("");
    if (!sandbox) clearGame(sessionId);
  }, [puzzle.solution.length, sandbox, sessionId]);

  const setAssists = useCallback((next: AssistSettings) => {
    setAssistsState(next);
    saveAssistSettings(next);
  }, []);

  return {
    cells,
    completed,
    elapsedSeconds,
    historyCount: history.length,
    redoCount: redoStack.length,
    mistakeCell: mistakeCells[0],
    mistakeCells,
    mistakes,
    playMode,
    setPlayMode,
    assists,
    setAssists,
    cycleMark,
    resumed,
    stats,
    tool,
    selectTool,
    boardMode,
    setBoardMode,
    checkMessage,
    beginStroke,
    paintCell,
    endStroke,
    makeMove,
    undo,
    redo,
    checkBoard,
    reset,
  };
}
